import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  Moon,
  Sun,
  Info,
  ChevronDown,
  ChevronUp,
  Eye,
  Tag,
  Palette as PaletteIcon,
  Save,
  ExternalLink,
  X,
  Trash2,
  Dices,
} from "lucide-react";
import { Checkbox } from "./ui/Checkbox";
import { HexColorPicker, HexColorInput } from "react-colorful";
import Tooltip from "./ui/Tooltip";
import InfoModal from "./InfoModal";
import EyeDropper from "./EyeDropper";
import Visualizer from "./Visualizer/Visualizer";
import { generateRandomColor } from "../lib/colorUtils";

const colorBlindnessTypes = [
  { id: "normal", label: "Normal Vision" },
  { id: "protanopia", label: "Protanopia" },
  { id: "deuteranopia", label: "Deuteranopia" },
  { id: "tritanopia", label: "Tritanopia" },
  { id: "achromatopsia", label: "Achromatopsia" },
  { id: "protanomaly", label: "Protanomaly" },
  { id: "deuteranomaly", label: "Deuteranomaly" },
  { id: "tritanomaly", label: "Tritanomaly" },
  { id: "achromatomaly", label: "Achromatomaly" },
];

const Toolbar = ({
  isDark,
  setIsDark,
  visibleFields,
  setVisibleFields,
  colorBlindnessType,
  setColorBlindnessType,
  baseColor,
  tempColor,
  onColorChange,
  onColorChangeComplete,
  onEyeDropperSelect,
  onAddToPaletteForSwatches,
  visualizerColors,
  setVisualizerColors,
  addColorToPalette,
  isVisualizerOpen,
  toggleVisualizer,
  customPalette,
  setCustomPalette,
  onSaveCustomPalette,
  onClearCustomPalette,
  onExportCustomPaletteToVisualizer,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(true);
  const [isColorBlindnessOpen, setIsColorBlindnessOpen] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isCustomPaletteOpen, setIsCustomPaletteOpen] = useState(false);
  const [hasNewColors, setHasNewColors] = useState(() => {
    const saved = localStorage.getItem('hasNewCustomPaletteColors');
    return saved === 'true';
  });
  const toolbarRef = useRef(null);
  const infoModalRef = useRef(null);
  const currentColorRef = useRef(tempColor);
  const prevPaletteLength = useRef(customPalette?.length || 0);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useEffect(() => {
    const allChecked = Object.values(visibleFields).every(Boolean);
    setIsAllChecked(allChecked);
  }, [visibleFields]);

  useEffect(() => {
    currentColorRef.current = tempColor;
  }, [tempColor]);

  // Track when new colors are added to custom palette
  useEffect(() => {
    // If colors were added (length increased)
    if (customPalette && customPalette.length > prevPaletteLength.current && prevPaletteLength.current > 0) {
      setHasNewColors(true);
      localStorage.setItem('hasNewCustomPaletteColors', 'true');
    }
    // If this is the first render and there are colors, check if badge should be shown
    else if (prevPaletteLength.current === 0 && customPalette && customPalette.length > 0) {
      const saved = localStorage.getItem('hasNewCustomPaletteColors');
      // If no localStorage value set yet, show badge for existing colors
      if (saved === null) {
        setHasNewColors(true);
        localStorage.setItem('hasNewCustomPaletteColors', 'true');
      }
    }
    prevPaletteLength.current = customPalette?.length || 0;
  }, [customPalette]);

  // Clear badge when dropdown is opened
  useEffect(() => {
    if (isCustomPaletteOpen) {
      setHasNewColors(false);
      localStorage.setItem('hasNewCustomPaletteColors', 'false');
    }
  }, [isCustomPaletteOpen]);

  const handleOutsideClick = useCallback((event) => {
    if (toolbarRef.current && !toolbarRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
      setIsColorBlindnessOpen(false);
      setIsColorPickerOpen(false);
      setIsCustomPaletteOpen(false);
    }
  }, []);

  const handleModalOutsideClick = useCallback((event) => {
    if (infoModalRef.current && !infoModalRef.current.contains(event.target)) {
      setIsInfoModalOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    if (isInfoModalOpen) {
      document.addEventListener("mousedown", handleModalOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("mousedown", handleModalOutsideClick);
    };
  }, [handleOutsideClick, handleModalOutsideClick, isInfoModalOpen]);

  const toggleDarkMode = () => {
    setIsDark((prevMode) => !prevMode);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsColorBlindnessOpen(false);
    setIsColorPickerOpen(false);
    setIsCustomPaletteOpen(false);
  };

  const toggleColorBlindness = () => {
    setIsColorBlindnessOpen(!isColorBlindnessOpen);
    setIsDropdownOpen(false);
    setIsColorPickerOpen(false);
    setIsCustomPaletteOpen(false);
  };

  const toggleColorPicker = () => {
    setIsColorPickerOpen(!isColorPickerOpen);
    setIsDropdownOpen(false);
    setIsColorBlindnessOpen(false);
    setIsCustomPaletteOpen(false);
  };

  const toggleCustomPalette = () => {
    setIsCustomPaletteOpen(!isCustomPaletteOpen);
    setIsDropdownOpen(false);
    setIsColorBlindnessOpen(false);
    setIsColorPickerOpen(false);
  };

  const removeColorFromCustomPalette = (color) => {
    setCustomPalette(customPalette.filter(c => c !== color));
  };

  const handleCheckboxChange = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleShowAllChange = () => {
    const newValue = !isAllChecked;
    setIsAllChecked(newValue);
    setVisibleFields((prev) =>
      Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: newValue }), {}),
    );
  };

  const getFieldLabel = (field) => {
    if (["hsl", "hex", "rgb"].includes(field.toLowerCase())) {
      return field.toUpperCase();
    }
    return (
      field.charAt(0).toUpperCase() +
      field
        .slice(1)
        .replace(/([A-Z])/g, " $1")
        .trim()
    );
  };

  const openInfoModal = () => {
    setIsInfoModalOpen(true);
  };

  const closeInfoModal = () => {
    setIsInfoModalOpen(false);
  };

  const handleColorPickerChange = (color) => {
    currentColorRef.current = color;
    onColorChange(color);
  };

  const handleColorPickerChangeComplete = () => {
    onColorChangeComplete(currentColorRef.current);
  };

  const handleRandomColor = () => {
    const newColor = generateRandomColor();
    onColorChangeComplete(newColor);
  };

  useEffect(() => {
    if (onAddToPaletteForSwatches) {
      onAddToPaletteForSwatches((color) => {
        console.log("Adding color to palette in Toolbar:", color);
        addColorToPalette(color);
      });
    }
  }, [addColorToPalette, onAddToPaletteForSwatches]);

  useEffect(() => {
    console.log("Visualizer colors in Toolbar:", visualizerColors);
  }, [visualizerColors]);

  const toolbarItemClass =
    "p-2 rounded-full transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500";

  const isAnyDropdownOpen =
    isDropdownOpen || isColorBlindnessOpen || isColorPickerOpen || isCustomPaletteOpen;

  const dropdownClass =
    "absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 transition-all duration-300 ease-in-out";
  const openDropdownClass = "opacity-100 scale-100 translate-y-0";
  const closedDropdownClass =
    "opacity-0 scale-95 translate-y-2 pointer-events-none";

  return (
    <>
      <div
        ref={toolbarRef}
        data-toolbar
        className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[60] backdrop-blur-md bg-white/70 dark:bg-gray-800/70 rounded-full shadow-lg px-4 py-2 flex items-center space-x-2 transition-colors duration-300"
      >
        <div className="relative">
          <button
            onClick={toggleColorPicker}
            className={`${toolbarItemClass} w-8 h-8 border-2 border-gray-300 dark:border-gray-600 transition-colors duration-300`}
            style={{ backgroundColor: tempColor }}
          />
          <div
            className={`${dropdownClass} ${isColorPickerOpen ? openDropdownClass : closedDropdownClass}`}
          >
            <HexColorPicker
              color={tempColor}
              onChange={handleColorPickerChange}
              onMouseUp={handleColorPickerChangeComplete}
              onTouchEnd={handleColorPickerChangeComplete}
            />
            <div className="mt-2">
              <HexColorInput
                color={tempColor}
                onChange={handleColorPickerChange}
                onBlur={handleColorPickerChangeComplete}
                prefixed
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 transition-colors duration-300"
              />
            </div>
          </div>
        </div>
        <button onClick={handleRandomColor} className={toolbarItemClass} title="Generate random color">
          <Dices className="h-5 w-5" />
        </button>
        <EyeDropper onColorSelect={onEyeDropperSelect} />
        <button onClick={toggleDarkMode} className={toolbarItemClass}>
          {isDark ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </button>
        <div className="relative">
            <button
              onClick={toggleDropdown}
              className={`${toolbarItemClass} flex items-center`}
            >
              <Tag className="h-5 w-5 mr-1" />
              {isDropdownOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
            <div
              className={`${dropdownClass} ${isDropdownOpen ? openDropdownClass : closedDropdownClass}`}
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <Checkbox
                    id="showAll"
                    checked={isAllChecked}
                    onCheckedChange={handleShowAllChange}
                  />
                  <label
                    htmlFor="showAll"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Show All
                  </label>
                </div>
                {Object.keys(visibleFields).map((field) => (
                  <div key={field} className="flex items-center space-x-2">
                    <Checkbox
                      id={field}
                      checked={visibleFields[field]}
                      onCheckedChange={() => handleCheckboxChange(field)}
                    />
                    <label
                      htmlFor={field}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {getFieldLabel(field)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
        </div>
        <div className="relative">
            <button
              onClick={toggleColorBlindness}
              className={`${toolbarItemClass} flex items-center`}
            >
              <Eye className="h-5 w-5" />
              {isColorBlindnessOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </button>
            <div
              className={`${dropdownClass} ${isColorBlindnessOpen ? openDropdownClass : closedDropdownClass}`}
            >
              <div className="space-y-2">
                {colorBlindnessTypes.map((type) => (
                  <div key={type.id} className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id={type.id}
                      name="colorBlindnessType"
                      value={type.id}
                      checked={colorBlindnessType === type.id}
                      onChange={() => setColorBlindnessType(type.id)}
                      className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                    />
                    <label
                      htmlFor={type.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {type.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
        </div>

        <div className="relative">
            <button
              onClick={toggleCustomPalette}
              className={`${toolbarItemClass} flex items-center relative`}
            >
              <PaletteIcon className="h-5 w-5 mr-1" />
              {isCustomPaletteOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
              {hasNewColors && !isCustomPaletteOpen && (
                <div className="absolute -top-0.5 -right-0.5 px-1 py-0.5 text-[8px] font-bold bg-red-500 text-white rounded-full leading-none">
                  NEW
                </div>
              )}
            </button>
            <div
              className={`${dropdownClass} ${isCustomPaletteOpen ? openDropdownClass : closedDropdownClass} min-w-[250px]`}
            >
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 pb-2 border-b border-gray-200 dark:border-gray-700">
                  Custom Palette
                </div>
                {customPalette && customPalette.length > 0 ? (
                  <>
                    <div className="grid grid-cols-5 gap-2 max-h-[200px] overflow-y-auto">
                      {customPalette.map((color, index) => (
                        <div key={`${color}-${index}`} className="relative group">
                          <div
                            className="w-10 h-10 rounded cursor-pointer border-2 border-gray-300 dark:border-gray-600 transition-transform hover:scale-110"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                          <button
                            onClick={() => removeColorFromCustomPalette(color)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <button
                        onClick={onSaveCustomPalette}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded transition-colors"
                      >
                        <Save className="h-4 w-4" />
                        Save Palette
                      </button>
                      <button
                        onClick={onExportCustomPaletteToVisualizer}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Send to Visualizer
                      </button>
                      <button
                        onClick={onClearCustomPalette}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                        Clear Palette
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                    No colors yet. Right-click any color to add it here.
                  </div>
                )}
              </div>
            </div>
        </div>

        <button onClick={openInfoModal} className={toolbarItemClass}>
          <Info className="h-5 w-5" />
        </button>
      </div>
      <Visualizer
        isOpen={isVisualizerOpen}
        onClose={toggleVisualizer}
        colors={visualizerColors}
        onColorsChange={setVisualizerColors}
        baseColor={baseColor}
        colorBlindnessType={colorBlindnessType}
        isDark={isDark}
      />
      {isInfoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={infoModalRef}>
            <InfoModal onClose={closeInfoModal} />
          </div>
        </div>
      )}
    </>
  );
};
export default Toolbar;
